/**
 * Runs a Toolbox tab script in a Web Worker.
 *
 * A toolbox is a JS file from the flow's `snapshots/toolboxScripts` folder (see
 * the server's controller/snapshotEditor/toolboxFile.js). That folder is shared
 * with agentContainer, so the code in it is not the dashboard's own and does not
 * run next to it: no DOM, no localStorage (where the access token lives), no
 * editor internals. It gets a `ctx` object whose every method is a message back
 * to this side, answered by the `handlers` the Toolbox tab passes in.
 *
 * File format — one self-contained script, no imports:
 *
 *   export default {
 *     title: "Word Master tools",
 *     buttons: [
 *       { id: "reload", label: "Reload preview", run: async (ctx) => ctx.reloadPreview() },
 *     ],
 *     lists: [
 *       {
 *         id: "levels",
 *         label: "Levels",
 *         load: async (ctx, { refresh }) => ctx.listLevels({ refresh }),
 *         onSelect: async (ctx, item) => { … },
 *       },
 *     ],
 *   };
 *
 * A list is for the tool that is "pick one of these and apply it": it is fetched
 * once and stays on the panel, so choosing again is one click rather than a
 * button, a wait and a dialog. The worker keeps the items it loaded and a
 * selection names one by index, so what `onSelect` receives is the script's own
 * object, not a copy that crossed the message boundary.
 */

import _ from "lodash";

/** What a script can ask for. Each name is a key of the `handlers` object. */
const TOOLBOX_CTX_METHODS = [
  "getSnapshot",
  "applyChange",
  "addAsset",
  "reloadPreview",
  "sendToPreview",
  "notify",
  "pick",
  "choose",
  "listLevels",
  "getLevel",
  "repoTree",
  "repoFiles",
  "libraryTree",
  "libraryFiles",
  "albert",
];

// The worker's own code. Kept as a string and started from a Blob so it needs
// no bundler support, and so the script it loads shares nothing with this
// bundle. `export default` is rewritten to an assignment because a classic
// worker's importScripts cannot load a module.
// Exported for the tests, which run it in a fake worker scope.
export const WORKER_BOOTSTRAP = `
"use strict";
const METHODS = ${JSON.stringify(TOOLBOX_CTX_METHODS)};
let toolbox = null;
let nextCallId = 1;
const pending = new Map();
const listItems = new Map();

function makeCtx(runId) {
  const ctx = {};
  METHODS.forEach((method) => {
    ctx[method] = (...args) => new Promise((resolve, reject) => {
      const callId = nextCallId++;
      pending.set(callId, { resolve, reject });
      self.postMessage({ type: "call", runId, callId, method, args });
    });
  });
  return Object.freeze(ctx);
}

function describe(error) {
  return (error && (error.message || String(error))) || "Unknown error";
}

self.onmessage = async (event) => {
  const message = event.data || {};
  if (message.type === "load") {
    try {
      // A classic worker cannot load a module, so every \`export\` is rewritten
      // away: the default one becomes the toolbox itself, a named one keeps its
      // declaration without the keyword, and a list export is dropped. Without
      // this a script that exports a helper fails to load at all, with nothing
      // but "Unexpected token 'export'" to go on.
      //
      // Anchored to the start of a line, because the words also turn up inside
      // the script's own comments and strings — a header that explains what to
      // \`export default\` used to consume the rewrite and leave the real one.
      const source = String(message.source)
        .replace(/^[ \\t]*export\\s+default\\b/m, "self.__toolbox__ =")
        .replace(/^[ \\t]*export\\s+(?=(?:const|let|var|function|class|async)\\b)/gm, "")
        .replace(/^[ \\t]*export\\s*\\{[^}]*\\}[ \\t]*;?[ \\t]*$/gm, "");
      // An \`import\` cannot be rewritten away — there is nothing to import from
      // in here. Said plainly, because the alternative is a bare SyntaxError.
      if (/^[ \\t]*import[\\s{*'"]/m.test(source)) {
        throw new Error("A toolbox cannot import anything: it runs alone in a worker. Inline what it needs.");
      }
      // Cleared first: a failed load must not leave the previous toolbox in place
      // and look like it worked.
      self.__toolbox__ = undefined;
      const url = URL.createObjectURL(new Blob([source], { type: "text/javascript" }));
      try { importScripts(url); } finally { URL.revokeObjectURL(url); }
      toolbox = self.__toolbox__;
      if (toolbox === undefined) {
        throw new Error("A toolbox must \`export default\` its object; this script exported nothing.");
      }
      // Either half is enough: a toolbox of lists alone is as complete as one
      // of buttons alone, and a port that produces only a level list used to be
      // rejected here with a message about buttons.
      if (!toolbox || (!Array.isArray(toolbox.buttons) && !Array.isArray(toolbox.lists))) {
        throw new Error("A toolbox must \`export default\` an object with \`buttons\` or \`lists\`.");
      }
      self.postMessage({
        type: "loaded",
        title: typeof toolbox.title === "string" ? toolbox.title : null,
        lists: (Array.isArray(toolbox.lists) ? toolbox.lists : [])
          .filter((list) => list && typeof list.load === "function")
          .map((list, index) => ({
            id: String(list.id || "list" + index),
            label: String(list.label || list.id || "List " + (index + 1)),
            description: typeof list.description === "string" ? list.description : null,
            upload: list.upload && typeof list.upload === "object" ? {
              label: String(list.upload.label || "Upload folder"),
              libraryPath: String(list.upload.libraryPath || ""),
              folder: list.upload.folder !== false,
              accept: typeof list.upload.accept === "string" ? list.upload.accept : null,
            } : null,
          })),
        buttons: (Array.isArray(toolbox.buttons) ? toolbox.buttons : [])
          .filter((button) => button && typeof button.run === "function")
          .map((button, index) => ({
            id: String(button.id || index),
            label: String(button.label || button.id || "Button " + (index + 1)),
            description: typeof button.description === "string" ? button.description : null,
            needsSnapshot: button.needsSnapshot !== false,
          })),
      });
    } catch (error) {
      self.postMessage({ type: "loadError", message: describe(error) });
    }
    return;
  }

  if (message.type === "reply") {
    const call = pending.get(message.callId);
    if (!call) return;
    pending.delete(message.callId);
    if (message.ok) call.resolve(message.value);
    else call.reject(new Error(message.error));
    return;
  }

  if (message.type === "loadList" || message.type === "select" || message.type === "uploaded") {
    const list = toolbox && (Array.isArray(toolbox.lists) ? toolbox.lists : []).find(
      (candidate, index) => candidate && String(candidate.id || "list" + index) === message.listId
    );
    if (!list) {
      self.postMessage({ type: "failed", runId: message.runId, message: "No such list." });
      return;
    }
    try {
      if (message.type === "loadList") {
        const result = await list.load(makeCtx(message.runId), { refresh: message.refresh === true });
        // A list answers with its rows, or with { items, source } — where the
        // rows came from, which the script works out while it loads them.
        const items = Array.isArray(result) ? result : (result && Array.isArray(result.items) ? result.items : []);
        const source = Array.isArray(result) ? null : (result && result.source) || null;
        listItems.set(message.listId, items);
        self.postMessage({ type: "done", runId: message.runId, value: { items: items, source: source } });
      } else if (message.type === "uploaded") {
        if (typeof list.onUploaded === "function") {
          await list.onUploaded(makeCtx(message.runId), message.info);
        }
        self.postMessage({ type: "done", runId: message.runId });
      } else {
        const items = listItems.get(message.listId) || [];
        if (typeof list.onSelect === "function") {
          await list.onSelect(makeCtx(message.runId), items[message.index]);
        }
        self.postMessage({ type: "done", runId: message.runId });
      }
    } catch (error) {
      self.postMessage({ type: "failed", runId: message.runId, message: describe(error) });
    }
    return;
  }

  if (message.type === "run") {
    const button = toolbox && (Array.isArray(toolbox.buttons) ? toolbox.buttons : []).find(
      (candidate, index) => candidate && String(candidate.id || index) === message.buttonId
    );
    if (!button) {
      self.postMessage({ type: "failed", runId: message.runId, message: "No such button." });
      return;
    }
    try {
      await button.run(makeCtx(message.runId));
      self.postMessage({ type: "done", runId: message.runId });
    } catch (error) {
      self.postMessage({ type: "failed", runId: message.runId, message: describe(error) });
    }
  }
};
`;

function defaultCreateWorker() {
  const url = URL.createObjectURL(new Blob([WORKER_BOOTSTRAP], { type: "text/javascript" }));
  const worker = new Worker(url);
  // The worker has already fetched its code by the time it runs; the URL is
  // only needed for construction.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return worker;
}

/**
 * Start a worker on one toolbox source.
 *
 * @param {object}   opts
 * @param {string}   opts.source    the toolbox file's text
 * @param {object}   opts.handlers  `{ [method]: async (...args) => value }` for TOOLBOX_CTX_METHODS
 * @param {function} [opts.createWorker] injected by tests
 * @returns {{ loaded: Promise<{title, buttons, lists}>, run(buttonId): Promise<void>,
 *            loadList(listId): Promise<Array>, select(listId, index): Promise<void>,
 *            uploaded(listId, info): Promise<void>, terminate(): void }}
 */
export function startToolboxRunner({ source, handlers, createWorker = defaultCreateWorker }) {
  const worker = createWorker();
  const runs = new Map();
  let nextRunId = 1;
  let terminated = false;
  let settleLoad;

  const loaded = new Promise((resolve, reject) => {
    settleLoad = { resolve, reject };
  });

  async function answer(message) {
    const handler = handlers && handlers[message.method];
    let reply;
    try {
      if (!TOOLBOX_CTX_METHODS.includes(message.method) || typeof handler !== "function") {
        throw new Error(`ctx.${message.method} is not available.`);
      }
      reply = { ok: true, value: await handler(...(message.args || [])) };
    } catch (error) {
      reply = { ok: false, error: (error && error.message) || String(error) };
    }
    if (terminated) return;
    worker.postMessage({ type: "reply", callId: message.callId, ...reply });
  }

  worker.onmessage = (event) => {
    const message = event.data || {};
    switch (message.type) {
    case "loaded":
      settleLoad.resolve({
        title: message.title,
        buttons: message.buttons,
        lists: message.lists || [],
      });
      break;
    case "loadError":
      settleLoad.reject(new Error(message.message));
      break;
    case "call":
      void answer(message);
      break;
    case "done":
    case "failed": {
      const run = runs.get(message.runId);
      if (!run) break;
      runs.delete(message.runId);
      if (message.type === "done") run.resolve(message.value);
      else run.reject(new Error(message.message));
      break;
    }
    default:
      break;
    }
  };

  // A syntax error in the toolbox surfaces here rather than as a loadError.
  worker.onerror = (event) => {
    if (event && typeof event.preventDefault === "function") event.preventDefault();
    const error = new Error((event && event.message) || "The toolbox script failed to load.");
    settleLoad.reject(error);
    runs.forEach((run) => run.reject(error));
    runs.clear();
  };

  worker.postMessage({ type: "load", source });

  /** Start one unit of work in the worker and settle when it reports back. */
  function dispatch(message) {
    if (terminated) return Promise.reject(new Error("The toolbox was stopped."));
    const runId = nextRunId++;
    return new Promise((resolve, reject) => {
      runs.set(runId, { resolve, reject });
      worker.postMessage({ ...message, runId });
    });
  }

  return {
    loaded,
    run(buttonId) {
      return dispatch({ type: "run", buttonId: String(buttonId) });
    },
    loadList(listId, { refresh = false } = {}) {
      return dispatch({ type: "loadList", listId: String(listId), refresh });
    },
    select(listId, index) {
      return dispatch({ type: "select", listId: String(listId), index });
    },
    uploaded(listId, info) {
      return dispatch({ type: "uploaded", listId: String(listId), info });
    },
    terminate() {
      if (terminated) return;
      terminated = true;
      worker.terminate();
      const error = new Error("The toolbox was stopped.");
      settleLoad.reject(error);
      runs.forEach((run) => run.reject(error));
      runs.clear();
    },
  };
}

/**
 * Apply a script's `ctx.applyChange` patches to a copy of the snapshot.
 *
 * `[{ path, value }]` sets, `[{ path, remove: true }]` deletes. Paths are lodash
 * paths (`"globalVariables.level"`, `["assets", "image-3"]`).
 */
export function applyToolboxPatches(snapshot, patches) {
  if (!Array.isArray(patches)) throw new Error("applyChange takes an array of { path, value }.");
  const next = _.cloneDeep(snapshot);
  patches.forEach((patch) => {
    const path = patch && patch.path;
    if (!(typeof path === "string" && path) && !(Array.isArray(path) && path.length)) {
      throw new Error("Every patch needs a path.");
    }
    if (patch.remove) _.unset(next, path);
    else _.set(next, path, patch.value);
  });
  return next;
}
